import { useEffect } from 'react';

import { useFormContext } from '@/components/react-hook-form';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import {
  DispositiveProvisionsFormRecipient,
  RecipientKind,
} from '../../DispositiveProvisionsForm.types';
import { SURVIVING_SPOUSE_SENTINEL } from '../../hooks/useRecipientOptions';
import { RecipientsBasePath } from '../DispositiveProvisionsRecipient';

interface UseSyncRecipientKindInput {
  currentRecipient?: DispositiveProvisionsFormRecipient;
  recipientsBasePath: RecipientsBasePath;
  idx: number;
}

/**
 * Keep recipient.recipientKind in sync with recipientId selection
 */
export function useSyncRecipientKind({
  currentRecipient,
  recipientsBasePath,
  idx,
}: UseSyncRecipientKindInput) {
  const { setValue } = useFormContext<CombinedFormShape>();

  useEffect(() => {
    const recipientId = currentRecipient?.recipientId;
    const fieldPath =
      `${recipientsBasePath}.${idx}.recipientKind` as const satisfies CombinedPaths;

    if (!recipientId && !currentRecipient?.recipientKind) {
      return;
    }

    if (!recipientId) {
      // Clear kind if no recipient chosen
      setValue(fieldPath, null, {
        shouldValidate: false,
      });
      return;
    }

    let derivedKind:
      | DispositiveProvisionsFormRecipient['recipientKind']
      | null = null;
    if (recipientId === SURVIVING_SPOUSE_SENTINEL) {
      derivedKind = RecipientKind.SurvivingSpouse;
    } else {
      const pulidKind = getPulidKind(recipientId);
      switch (pulidKind) {
        case PulidKind.Entity:
          derivedKind = RecipientKind.Entity;
          break;
        case PulidKind.TestamentaryEntity:
          derivedKind = RecipientKind.TestamentaryEntity;
          break;
        case PulidKind.ClientProfile:
          derivedKind = RecipientKind.Individual;
          break;
        case PulidKind.ClientOrganization:
          derivedKind = RecipientKind.Organization;
          break;
        default:
          derivedKind = null;
      }
    }

    if (currentRecipient?.recipientKind !== derivedKind) {
      setValue(fieldPath, derivedKind, {
        shouldDirty: true,
        shouldValidate: false,
      });
    }
  }, [
    currentRecipient,
    currentRecipient?.recipientId,
    currentRecipient?.recipientKind,
    idx,
    recipientsBasePath,
    setValue,
  ]);
}
